import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Typography, Tooltip, Alert, Spin, Descriptions, Tag, Button, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_STATISTICS = {
  pushCount: 1286,
  pushDeviceCount: 352,
  upgradeSuccessCount: 298,
  upgradeFailedCount: 54,
  coverDeviceCount: 1500,
  firmwareName: '主控固件',
  firmwareVersion: 'V3.1.0',
  productName: '智能摄像机Q1',
  productId: 'PROD_Q1',
  updateTime: Math.floor(Date.now() / 1000),
};

// TODO: 联调时替换为接口数据
const MOCK_OTA_TASK = {
  createdAt: '2026-07-01 09:00:00',
  upgradeFirmwareVersionType: 1, // 0=不限版本, 1=指定版本
  upgradeType: 1, // 1=静默升级(强制), 2=定时升级
  upgradeStartTime: '02:00',
  upgradeEndTime: '04:00',
  isForcedUpgrade: 1,
  upgradeScopeType: 3, // 1=全部设备, 2=按区域, 3=按设备
  status: 2, // 1/3=待发布/已暂停, 2=发布中
  descLanguage: '{"1":"修复夜间模式白平衡问题，优化WiFi连接稳定性"}',
};

const MOCK_CHART_DATA = [
  { day: 1751212800, pushCount: 210, pushDeviceCount: 85, upgradeSuccessCount: 72 },
  { day: 1751299200, pushCount: 185, pushDeviceCount: 72, upgradeSuccessCount: 60 },
  { day: 1751385600, pushCount: 320, pushDeviceCount: 120, upgradeSuccessCount: 105 },
  { day: 1751472000, pushCount: 260, pushDeviceCount: 98, upgradeSuccessCount: 82 },
  { day: 1751558400, pushCount: 150, pushDeviceCount: 55, upgradeSuccessCount: 48 },
  { day: 1751644800, pushCount: 280, pushDeviceCount: 110, upgradeSuccessCount: 95 },
  { day: 1751731200, pushCount: 195, pushDeviceCount: 68, upgradeSuccessCount: 58 },
];

const FirmwareOtaUpgradeNum = () => {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('id') || '';
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const stats = MOCK_STATISTICS;
  const successRate = stats.pushDeviceCount > 0
    ? ((stats.upgradeSuccessCount / stats.pushDeviceCount) * 100).toFixed(1)
    : '-';

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading && chartRef.current) {
      const timer = setTimeout(() => {
        const pushCountData = MOCK_CHART_DATA.map(e => e.pushCount);
        const pushDeviceData = MOCK_CHART_DATA.map(e => e.pushDeviceCount);
        const successData = MOCK_CHART_DATA.map(e => e.upgradeSuccessCount);
        const dayData = MOCK_CHART_DATA.map(e => {
          const d = new Date(e.day * 1000);
          return `${d.getMonth() + 1}/${d.getDate()}`;
        });

        if (chartInstance.current) chartInstance.current.dispose();
        chartInstance.current = echarts.init(chartRef.current);

        const maxValue = Math.max(...pushCountData, ...pushDeviceData, ...successData, 0);
        const splitNumber = maxValue < 8 ? (maxValue || 1) : 8;

        chartInstance.current.setOption({
          title: { text: '升级概览', left: 'center' },
          legend: {
            data: ['推送次数', '推送设备数', '升级成功数'],
            top: 30,
            selectedMode: true,  // 图例可点击切换显示/隐藏
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross', crossStyle: { color: '#999' } },
          },
          dataZoom: [{ type: 'slider' }],
          grid: { top: 80, left: 60, right: 40, bottom: 60 },
          xAxis: {
            type: 'category',
            data: dayData,
            axisTick: { alignWithLabel: true },
            axisLine: { lineStyle: { color: '#999' } },
          },
          yAxis: {
            type: 'value',
            name: '数量',
            min: 0,
            minInterval: 1,
            splitNumber,
            axisLabel: { formatter: v => Math.floor(v) },
          },
          series: [
            {
              name: '推送次数',
              type: 'bar',
              data: pushCountData,
              barWidth: 22,
              itemStyle: { color: '#5470c6' },
              label: { show: true, position: 'top', formatter: p => p.value || '' },
            },
            {
              name: '推送设备数',
              type: 'bar',
              data: pushDeviceData,
              barWidth: 22,
              itemStyle: { color: '#fac858' },
              label: { show: true, position: 'top', formatter: p => p.value || '' },
            },
            {
              name: '升级成功数',
              type: 'line',
              data: successData,
              smooth: true,
              lineStyle: { color: '#91cc75', width: 2.5 },
              itemStyle: { color: '#91cc75' },
              symbol: 'circle',
              symbolSize: 6,
              label: { show: true, position: 'top', formatter: p => p.value || '' },
            },
          ],
        });

        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        setChartLoading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>设备升级统计</Title>

      <Spin spinning={loading}>
        <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12 }}>
          <Alert
            type="info"
            showIcon
            message={`自升级发起后，系统将每隔1小时自动执行一次数据统计与更新。上次统计时间：${new Date(stats.updateTime * 1000).toLocaleString()}`}
            style={{ marginBottom: 24, borderRadius: 8 }}
          />

          {/* ═══════════ 统计卡片 ═══════════ */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card bordered={false} bodyStyle={{ padding: '20px', textAlign: 'center' }} style={{ background: '#f5f7fa', borderRadius: 8 }}>
                <div style={{ fontSize: 14, color: '#606266', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  推送次数
                  <Tooltip title="对设备推送的总计升级次数">
                    <InfoCircleOutlined style={{ color: '#999', fontSize: 12, cursor: 'help' }} />
                  </Tooltip>
                </div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#303133' }}>{stats.pushCount.toLocaleString()}</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false} bodyStyle={{ padding: '20px', textAlign: 'center' }} style={{ background: '#f5f7fa', borderRadius: 8 }}>
                <div style={{ fontSize: 14, color: '#606266', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  推送设备
                  <Tooltip title="以设备维度进行统计的推送升级数（去重）">
                    <InfoCircleOutlined style={{ color: '#999', fontSize: 12, cursor: 'help' }} />
                  </Tooltip>
                </div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#303133' }}>{stats.pushDeviceCount.toLocaleString()}</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false} bodyStyle={{ padding: '20px', textAlign: 'center' }} style={{ background: '#f5f7fa', borderRadius: 8 }}>
                <div style={{ fontSize: 14, color: '#606266', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  设备数
                  <Tooltip title="升级成功的设备数量">
                    <InfoCircleOutlined style={{ color: '#999', fontSize: 12, cursor: 'help' }} />
                  </Tooltip>
                </div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#67c23a' }}>{stats.upgradeSuccessCount.toLocaleString()}</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false} bodyStyle={{ padding: '20px', textAlign: 'center' }} style={{ background: '#f5f7fa', borderRadius: 8 }}>
                <div style={{ fontSize: 14, color: '#606266', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  成功率
                  <Tooltip title="升级成功设备数 / 推送设备总数">
                    <InfoCircleOutlined style={{ color: '#999', fontSize: 12, cursor: 'help' }} />
                  </Tooltip>
                </div>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#67c23a' }}>{successRate}%</div>
              </Card>
            </Col>
          </Row>
        </Card>

        {/* ═══════════ 任务详情 ═══════════ */}
        <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12 }}>
          <Descriptions bordered size="small" column={4}>
            <Descriptions.Item label="固件版本">
              <Text strong>{stats.firmwareName} / {stats.firmwareVersion}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="任务创建时间">{MOCK_OTA_TASK.createdAt}</Descriptions.Item>
            <Descriptions.Item label="基于固件版本">
              {MOCK_OTA_TASK.upgradeFirmwareVersionType === 0
                ? '不限版本（任意版本均可升级）'
                : <Button type="link" size="small" style={{ padding: 0 }}>查看</Button>}
            </Descriptions.Item>
            <Descriptions.Item label="产品名称">{stats.productName}</Descriptions.Item>
            <Descriptions.Item label="产品ID"><Text code>{stats.productId}</Text></Descriptions.Item>
            <Descriptions.Item label="升级方式">
              <span>{MOCK_OTA_TASK.isForcedUpgrade ? '静默升级(强制)' : '静默升级'}</span>
              {MOCK_OTA_TASK.upgradeType === 2 && (
                <div style={{ fontSize: 12, color: '#909399', marginTop: 4 }}>
                  推送时间点：<Tag color="red" style={{ marginLeft: 4 }}>02:00</Tag><Tag color="red">02:30</Tag><Tag color="red">03:00</Tag><Tag color="red">03:30</Tag><Tag color="red">04:00</Tag>
                </div>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="操作">
              <Space size={4}>
                {[1, 3].includes(MOCK_OTA_TASK.status) && <Button type="link" size="small">发布升级</Button>}
                {MOCK_OTA_TASK.status === 2 && <Button type="link" size="small">暂停发布</Button>}
                {[2, 3].includes(MOCK_OTA_TASK.status) && <Button type="link" size="small">终止发布</Button>}
                <Button type="primary" size="small" onClick={() => window.open(`/device/firmware/ota/devices?id=${taskId}`, '_self')}>查看升级明细</Button>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="待升级设备">
              {MOCK_OTA_TASK.upgradeScopeType === 1 && '全部设备'}
              {MOCK_OTA_TASK.upgradeScopeType === 2 && <Button type="link" size="small" style={{ padding: 0 }}>查看区域</Button>}
              {MOCK_OTA_TASK.upgradeScopeType === 3 && <Button type="link" size="small" style={{ padding: 0 }}>查看设备</Button>}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* ═══════════ 图表 ═══════════ */}
        <Card bordered={false} bodyStyle={{ padding: '32px 40px' }}>
          <Spin spinning={chartLoading}>
            <div ref={chartRef} style={{ width: '100%', height: 400 }} />
          </Spin>
        </Card>
      </Spin>
    </div>
  );
};

export default FirmwareOtaUpgradeNum;
